import React, { useState, useEffect, useCallback } from 'react'
import {
  TextField,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Collapse,
  InputAdornment,
  IconButton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useNavigate } from 'react-router-dom'
import { axiosi } from '../config/axios'

export const SearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()

  const handleSearch = useCallback(async () => {
    if (searchQuery.trim().length === 0) {
      setSearchResults([])
      return
    }

    setIsLoading(true)
    try {
      const response = await axiosi.get('/products', { params: { q: searchQuery, limit: 8 } })
      const results = response.data.data || response.data || []

      setSearchResults(Array.isArray(results) ? results.slice(0, 8) : [])
      setShowResults(true)
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults([])
    } finally {
      setIsLoading(false)
    }
  }, [searchQuery])

  // Debounce search (shorter for snappier UX)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        handleSearch()
      } else {
        setSearchResults([])
        setShowResults(false)
      }
    }, 150)

    return () => clearTimeout(timer)
  }, [searchQuery, handleSearch])

  const handleSelectProduct = (productId) => {
    navigate(`/product-details/${productId}`)
    setSearchQuery('')
    setShowResults(false)
    setSearchResults([])
  }

  const handleClear = () => {
    setSearchQuery('')
    setShowResults(false)
    setSearchResults([])
  }

  return (
    <Box sx={{ position: 'relative', width: isMobile ? '100%' : '400px' }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search perfumes by name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && searchQuery.trim().length > 0) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
            setShowResults(false)
          }
        }}
        onFocus={() => searchQuery && setShowResults(true)}
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#ddd',
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            </InputAdornment>
          ),
          endAdornment: (
            searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClear}
                  edge="end"
                  sx={{ p: 0 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          ),
        }}
      />

      {/* Search Results Dropdown */}
      <Collapse in={showResults && (searchResults.length > 0 || isLoading)} unmountOnExit>
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : searchResults.length > 0 ? (
            <List sx={{ p: 0 }}>
              {searchResults.map((product, index) => (
                <ListItemButton
                  key={index}
                  onClick={() => handleSelectProduct(product._id)}
                  sx={{
                    borderBottom: index < searchResults.length - 1 ? '1px solid #eee' : 'none',
                    '&:hover': {
                      backgroundColor: '#f9f9f9',
                    },
                    py: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {product.images && product.images[0] && (
                      <Box
                        component="img"
                        src={product.images[0]}
                        alt={product.title}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          mr: 2,
                          objectFit: 'cover',
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <ListItemText
                        primary={product.title}
                        secondary={`Rs. ${product.price}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </Box>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <SearchIcon sx={{ mb: 1, opacity: 0.5 }} />
              <p>No perfumes found</p>
            </Box>
          )}
        </Paper>
      </Collapse>
    </Box>
  )
}
